/**
 * ChatInputBar — Chat input with toolbar, autocomplete, voice controls.
 * Rule 94: Modüler — input ayrı component.
 */
import { AudioLines, Hash, Mic, MicOff, Paperclip, Send, Volume2, VolumeX } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import { SLASH_COMMANDS } from './useSlashCommands'

interface Props {
  input: string
  setInput: (v: string) => void
  sending: boolean
  onSend: () => void
  listening: boolean
  toggleMic: () => void
  ttsEnabled: boolean
  toggleTTS: () => void
  activeAgentName: string | undefined
  skillsList: Array<{ id: number; name: string }>
  onVoiceMode?: () => void
  voiceModeActive?: boolean
}

export default function ChatInputBar({
  input, setInput, sending, onSend,
  listening, toggleMic, ttsEnabled, toggleTTS,
  activeAgentName, skillsList, onVoiceMode, voiceModeActive,
}: Props) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [selectedCommandIdx, setSelectedCommandIdx] = useState(-1)

  // Listen for runai:chat-insert events to insert text into the input
  useEffect(() => {
    const handler = (e: Event) => {
      const text = (e as CustomEvent).detail as string
      if (text) setInput((input ?? '') + text)
    }
    window.addEventListener('runai:chat-insert', handler)
    return () => window.removeEventListener('runai:chat-insert', handler)
  }, [setInput])

  // Always-on system icons — open respective windows
  const openWindow = useCallback((windowId: string) => {
    window.dispatchEvent(new CustomEvent('runai:open-window', { detail: windowId }))
  }, [])
  void openWindow // used via toolbar icon handlers below

  const slashMatches = input.startsWith('/') && !input.includes(' ') && input.length > 0
    ? SLASH_COMMANDS.filter(c => c.cmd.startsWith(input.toLowerCase()))
    : []

  const skillQuery = input.startsWith('/skill ') ? input.slice(7).toLowerCase() : ''
  const skillMatches = skillQuery ? skillsList.filter(s => s.name.toLowerCase().includes(skillQuery)).slice(0, 8) : []

  return (
    <div style={{
      padding: 10, borderTop: '1px solid var(--os-border)',
      display: 'flex', gap: 6, alignItems: 'flex-end', flexShrink: 0,
      position: 'relative',
    }}>
      {/* Slash command autocomplete */}
      {slashMatches.length > 0 && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 10, right: 10,
          background: 'var(--os-surface)', border: '1px solid var(--os-border)',
          borderRadius: 8, padding: 4, zIndex: 100,
          boxShadow: '0 -4px 16px rgba(0,0,0,0.3)',
        }}>
          {slashMatches.map((c, idx) => (
            <button key={c.cmd} onClick={() => { setInput(c.cmd + ' '); setSelectedCommandIdx(-1) }} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 8px', background: selectedCommandIdx === idx ? 'var(--os-hover)' : 'transparent',
              border: 'none', borderRadius: 5, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
              color: 'var(--os-text)', fontSize: 11, transition: 'background 0.1s',
            }}
              onMouseEnter={() => setSelectedCommandIdx(idx)}
              onMouseLeave={() => setSelectedCommandIdx(-1)}
            >
              <span style={{ color: 'var(--os-accent)', fontWeight: 700, minWidth: 70 }}>{c.cmd}</span>
              <span style={{ color: 'var(--os-muted)', fontSize: 10 }}>{c.desc}</span>
            </button>
          ))}
          <div style={{ fontSize: 9, color: 'var(--os-muted)', padding: '2px 8px', borderTop: '1px solid var(--os-border)', marginTop: 2 }}>
            Tab/Enter to select
          </div>
        </div>
      )}

      {/* Skill autocomplete */}
      {skillMatches.length > 0 && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 10, right: 10,
          background: 'var(--os-surface)', border: '1px solid var(--os-border)',
          borderRadius: 8, padding: 4, zIndex: 100,
          boxShadow: '0 -4px 16px rgba(0,0,0,0.3)',
          maxHeight: 200, overflowY: 'auto',
        }}>
          {skillMatches.map((s, idx) => (
            <button key={s.id} onClick={() => setInput(`/skill run ${s.name}`)} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 8px', background: selectedCommandIdx === idx ? 'var(--os-hover)' : 'transparent',
              border: 'none', borderRadius: 5, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
              color: 'var(--os-text)', fontSize: 11,
            }}
              onMouseEnter={() => setSelectedCommandIdx(idx)}
              onMouseLeave={() => setSelectedCommandIdx(-1)}
            >
              <span style={{ color: 'var(--os-accent)', fontWeight: 600 }}>⚡</span>
              <span>{s.name}</span>
            </button>
          ))}
        </div>
      )}

      <div className="os-chat-input-container" style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        background: 'var(--os-input-bg)',
        border: '1px solid var(--os-border)',
        borderRadius: 10, overflow: 'hidden',
      }}
        onFocus={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--os-accent)' }}
        onBlur={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--os-border)' }}
      >
        <textarea
          ref={inputRef}
          value={input}
          disabled={!user}
          aria-label={user ? `Message ${activeAgentName ?? 'My Agent'}` : 'Sign up to chat'}
          aria-multiline="true"
          aria-disabled={!user}
          onChange={e => { setInput(e.target.value); setSelectedCommandIdx(-1) }}
          onKeyDown={e => {
            // Slash command keyboard nav
            if (slashMatches.length > 0) {
              if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedCommandIdx(p => p < slashMatches.length - 1 ? p + 1 : 0); return }
              if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedCommandIdx(p => p > 0 ? p - 1 : slashMatches.length - 1); return }
              if (e.key === 'Tab' || (e.key === 'Enter' && selectedCommandIdx >= 0)) {
                e.preventDefault()
                setInput((selectedCommandIdx >= 0 ? slashMatches[selectedCommandIdx] : slashMatches[0]).cmd + ' ')
                setSelectedCommandIdx(-1); return
              }
            }
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend() }
          }}
          onClick={() => { if (!user) navigate('/register') }}
          placeholder={user ? `Message ${activeAgentName ?? 'My Agent'}... (/ for commands)` : 'Sign up to chat →'}
          rows={3}
          style={{
            width: '100%', padding: '8px 10px', boxSizing: 'border-box' as const,
            background: 'transparent',
            backgroundColor: 'transparent',
            border: 'none', outline: 'none',
            color: 'var(--os-text)',
            caretColor: 'var(--os-text)',
            fontSize: 11.5, resize: 'none' as const,
            fontFamily: 'inherit', lineHeight: 1.5,
            WebkitTextFillColor: 'var(--os-text)',
          }}
        />
        {/* Toolbar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '4px 8px', borderTop: '1px solid var(--os-border)',
          background: 'var(--os-surface)',
        }}>
          <ToolButton onClick={toggleMic} title={listening ? 'Stop mic' : 'Voice input'} active={listening} activeColor="#10b981">
            {listening ? <MicOff size={12} /> : <Mic size={12} />}
          </ToolButton>
          <ToolButton onClick={toggleTTS} title={ttsEnabled ? 'TTS off' : 'TTS on'} active={ttsEnabled} activeColor="#a855f7">
            {ttsEnabled ? <Volume2 size={12} /> : <VolumeX size={12} />}
          </ToolButton>
          {onVoiceMode && (
            <ToolButton onClick={onVoiceMode} title="Voice mode" active={voiceModeActive} activeColor="#3b82f6">
              <AudioLines size={12} />
            </ToolButton>
          )}
          <ToolButton onClick={() => setInput(input + '#')} title="Mention / tag">
            <Hash size={12} />
          </ToolButton>
          <ToolButton title="Attach file (coming soon)" disabled>
            <Paperclip size={12} />
          </ToolButton>
          <div style={{ flex: 1 }} />
          <button onClick={onSend} disabled={!input.trim() || sending}
            style={{
              height: 26, padding: '0 10px', borderRadius: 6, flexShrink: 0,
              background: input.trim() && !sending ? 'var(--os-accent)' : 'var(--os-hover)',
              border: 'none', cursor: input.trim() && !sending ? 'pointer' : 'default',
              color: input.trim() && !sending ? '#fff' : 'var(--os-muted)',
              display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600,
            }}>
            <Send size={11} /> Send
          </button>
        </div>
      </div>
    </div>
  )
}

/* ── Toolbar Button ── */
function ToolButton({ onClick, title, active, activeColor, disabled, children }: {
  onClick?: () => void; title: string; active?: boolean; activeColor?: string; disabled?: boolean; children: React.ReactNode
}) {
  return (
    <button onClick={onClick} title={title} disabled={disabled}
      style={{
        width: 26, height: 26, borderRadius: 6, flexShrink: 0,
        background: active ? `${activeColor}18` : 'transparent',
        border: `1px solid ${active ? activeColor : 'transparent'}`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: active ? activeColor : 'var(--os-muted)',
        opacity: disabled ? 0.4 : 1,
      }}>
      {children}
    </button>
  )
}
